.empty {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.nowrap {
  white-space: nowrap;
}

.mutedSmall {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.checkRow {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 26px;
  padding: 2px 4px;
  border-radius: var(--radius-xs);
  cursor: pointer;
}

/* A ticked item: green box, a light green ground, the name muted and struck through
   (like Outlook's completed tasks); the tick time keeps its green check. */
.checkRowDone {
  background: color-mix(in srgb, var(--success) 8%, var(--surface));
}

.checkRow.checkRowDone input {
  accent-color: var(--success);
}

.checkRowDone .checkName {
  color: var(--text-muted);
  text-decoration: line-through;
  text-decoration-thickness: 1px;
  text-decoration-color: color-mix(in srgb, var(--text-muted) 70%, transparent);
}

.checkRowDone .tickTime {
  color: var(--success);
}

.checkRow input {
  flex: none;
  /* One size below the 14px name text. */
  width: 13px;
  height: 13px;
  margin: 0;
  accent-color: var(--accent);
}

/* Touch: the whole row is the tap target (the label), 44px high; the box stays small. */
@media (pointer: coarse) {
  .checkRow {
    min-height: 44px;
  }

  .checkRow input {
    width: 16px;
    height: 16px;
  }
}

/* ---- check-in card: planned time column, tick time (2026-10b §8) ---- */
/* Only timed items have the column: it is as wide as its "HH:mm" text (tabular digits), so an
   untimed item starts its name right after the box. */
.planCol {
  flex: none;
  color: var(--text-muted);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.checkName {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tickTime {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 2px;
  color: var(--text-muted);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.tickTime svg {
  width: 12px;
  height: 12px;
  color: var(--success);
}

.todoDone {
  color: var(--text-muted);
  text-decoration: line-through;
}

/* Items unticked for three days stay in view below the main list: what is still to do must be
   visible at a glance, so the group is never collapsed. */
.faded {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
}

.fadedTitle {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

/* Between the timed items and the untimed ones. */
.timedDivider {
  height: 0;
  margin: 2px 4px;
  border-top: 1px solid var(--border);
}

/* ---- summary ---- */
.stack {
  display: flex;
  gap: 2px;
  height: 8px;
  overflow: hidden;
  border-radius: 4px;
  background: var(--surface-muted);
}

.stack span {
  min-width: 0;
  flex-basis: 0;
}

.bars {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.bars li {
  display: grid;
  grid-template-columns: 1fr auto;
  row-gap: 3px;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.barLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.barValue {
  font-variant-numeric: tabular-nums;
}

.barTrack {
  grid-column: 1 / -1;
  height: 6px;
  border-radius: 3px;
  background: var(--surface-muted);
  overflow: hidden;
}

.barTrack span {
  display: block;
  height: 100%;
  border-radius: 3px;
}

/* ---- quick mark: one vertical tile per project, no empty slots (ui-redesign §4.4) ---- */
.tiles {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tile {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  padding: 0 12px 0 0;
  border: 0;
  /* 6px like the blocks (2026-10b §4.5), softer than the old 8px "arc frame" look. */
  border-radius: var(--radius-block);
  /* Project-colour tints: 14% at rest, 24% hovered; 30% Once, 36% Locked (§4.5). */
  background: color-mix(in srgb, var(--tile-color) 14%, var(--white));
  color: var(--text);
  text-align: left;
  /* A consumed "once" fades its selection out over 120 ms (§4.2). */
  transition:
    background-color 120ms ease-out,
    box-shadow 120ms ease-out;
  /* Touch: no double-tap zoom, no long-press callout or selection (§4.2). */
  touch-action: manipulation;
  -webkit-touch-callout: none;
  user-select: none;
  /* Mouse and pen can drag the tile onto the timeline (2026-10c §2). */
  cursor: grab;
}

/* While dragging, the pointer is captured by the tile, so the tile's cursor shows everywhere. */
.tileDragging {
  cursor: grabbing;
}

.tileDragging {
  /* The tile stays in place as a faint placeholder; the drag card carries the project. */
  opacity: 0.45;
}

.tileDragging[data-drop-target='none'],
.tileDragging[data-drop-target='blocked'] {
  cursor: not-allowed;
}

/* A top-level tile and its expand control (project hierarchy). */
.tileRow {
  display: flex;
  align-items: stretch;
  gap: 4px;
}

.tileRow > .tile {
  flex: 1;
  min-width: 0;
  width: auto;
}

.tileExpand {
  display: inline-flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 28px;
  padding: 0 4px;
  border: 1px solid var(--border);
  border-radius: var(--radius-block);
  background: var(--surface);
  color: var(--text-muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.tileExpand:hover {
  background: var(--surface-muted);
}

.tileChevron {
  width: 6px;
  height: 6px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg) translateY(-2px);
}

.tileChevronOpen {
  transform: rotate(225deg) translateY(-2px);
}

/* Child tiles sit under their parent, indented, with the same tile geometry. */
.childTiles {
  margin: 4px 0 0 16px;
}

.earlierToggle {
  padding: 4px 8px;
  border: 0;
  background: transparent;
  color: var(--accent-700);
  font-size: var(--font-size-sm);
  text-align: left;
}

.tile:hover {
  background: color-mix(in srgb, var(--tile-color) 24%, var(--white));
}

/* The card that follows the pointer during a tile drag (rendered to the body, ignores input). */
.dragCard {
  position: fixed;
  z-index: 55;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 168px;
  height: 34px;
  padding-right: 12px;
  overflow: hidden;
  border-radius: var(--radius-block);
  background: color-mix(in srgb, var(--tile-color) 30%, var(--white));
  box-shadow: var(--shadow-pop);
  color: var(--text);
  opacity: 0.95;
  pointer-events: none;
  user-select: none;
}

.dragCard[data-allowed='false'] {
  opacity: 0.6;
}

/* Selection, variant A of §4.5: inset strokes in the project colour itself, never the accent.
   The 4px left bar sits on top of the stroke, so the double line shows on three sides only. */
.tileOnce {
  background: color-mix(in srgb, var(--tile-color) 30%, var(--white));
  box-shadow: inset 0 0 0 2px var(--tile-color);
}

.tileLocked {
  background: color-mix(in srgb, var(--tile-color) 36%, var(--white));
  /* 2px outer line, 2px white gap, 2px inner line (the 1px inner line fails 3:1 on 8/10 colours). */
  box-shadow:
    inset 0 0 0 2px var(--tile-color),
    inset 0 0 0 4px var(--white),
    inset 0 0 0 6px var(--tile-color);
}

/* The focus ring is the global accessibility convention, not part of the selection (§4.5). */
.tile:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.tileBar {
  flex: none;
  align-self: stretch;
  width: 4px;
  border-radius: var(--radius-block) 0 0 var(--radius-block);
  background: var(--tile-color);
}

/* Ordinal of every tile; 1–9 are also the digit shortcuts (§4.8). Two-digit ordinals fit the
   same width, so every name starts on one line. --text is ≥ 9.38:1 on every tint used. */
.tileOrdinal {
  flex: none;
  width: 18px;
  color: var(--text-muted);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.tileName {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size);
  font-weight: 500;
}

.tileCategory {
  flex: none;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

/* State pill "✓ 一次" / "lock 连续" (§4.5): the non-colour cue of the two modes. */
.tilePill {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--white);
  box-shadow: inset 0 0 0 1px var(--tile-color);
  color: var(--text);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* Child tiles (design 2026-10-15 §1): about 60% of a top-level tile (48px -> 29px), same font
   size; no ordinal or category tag, the colour bar, selection and lock styles stay. */
.childTile {
  height: 29px;
}

/* Child tiles are one shade lighter than the parent's (design 2026-10-15 §1): about half the
   project-colour share in the background, the left bar mixed 60:40 with white; the text colour
   stays and the selected / locked outlines keep the original project colour. */
.tile.childTile {
  background: color-mix(in srgb, var(--tile-color) 7%, var(--white));
}

.tile.childTile.tileOnce {
  background: color-mix(in srgb, var(--tile-color) 15%, var(--white));
}

.tile.childTile.tileLocked {
  background: color-mix(in srgb, var(--tile-color) 18%, var(--white));
}

.tile.childTile:hover {
  background: color-mix(in srgb, var(--tile-color) 12%, var(--white));
}

.childTile .tileBar {
  background: color-mix(in srgb, var(--tile-color) 60%, var(--white));
}

/* Title-row actions of the quick-mark card: expand all, collapse all, then the "+" add button. */
.cardActions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.cardIconButton {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  touch-action: manipulation;
}

.cardIconButton:hover:not(:disabled) {
  background: var(--surface-muted);
  color: var(--text);
}

.cardIconButton:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -1px;
}

.cardIconButton:disabled {
  opacity: 0.35;
  cursor: default;
}

.cardIconButton svg {
  width: 18px;
  height: 18px;
}

@media (pointer: coarse) {
  .cardIconButton {
    width: 44px;
    height: 44px;
  }
}

/* Touch screens: 56px tiles (§4.4). */
@media (pointer: coarse) {
  .tile {
    height: 56px;
  }

  .tile.childTile {
    height: 34px;
  }
}

/* ---- compact tiles of the week view's quick-mark sidebar (settings-revisions-and-layout.md §3.4) ---- */
.tilesCompact {
  gap: 4px;
}

.compactTile {
  height: 36px;
  padding: 0 8px 0 0;
  gap: 6px;
}

.compactTile.childTile {
  height: 22px;
}

.compactTile .tileName {
  font-size: 13px;
}

/* The pill shrinks to its icon: the text cue lives in the banner. */
.compactPill {
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
}

/* The 132px sidebar of narrow windows: 40px tiles (§3.4). */
@media (max-width: 768px) {
  .compactTile {
    height: 40px;
  }

  .compactTile.childTile {
    height: 24px;
  }
}

/* Touch screens: 44px tiles. */
@media (pointer: coarse) {
  .compactTile {
    height: 44px;
  }

  .compactTile.childTile {
    height: 27px;
  }
}

/* ---- suggestions ---- */
.suggestions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* A suggestion row (ui-redesign §4.4): white, with a 3px accent bar on its left edge. */
.suggestion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-left: 3px solid var(--accent);
  border-radius: var(--radius-sm);
  background: var(--surface);
}

/* The row's secondary accent button (§4.4): accent text on the soft accent tint. */
.suggestion .suggestionAction {
  border-color: transparent;
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 500;
}

.suggestion .suggestionAction:hover:not(:disabled) {
  background: var(--accent-soft-strong);
}

.suggestionText {
  min-width: 0;
}

.suggestionTitle {
  font-weight: 500;
}

/* Mode banner (2026-10b §4.4): Once on --accent-soft, Locked on --accent-soft-strong, both
   with a 3px accent bar and --text copy (≥ 13:1 on either tint). */
.quickBanner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 28px;
  padding: 2px 4px 2px 9px;
  border-left: 3px solid var(--accent);
  border-radius: var(--radius-sm);
  background: var(--accent-soft);
  color: var(--text);
  font-size: var(--font-size-sm);
}

.quickBannerLocked {
  background: var(--accent-soft-strong);
}

.quickBannerText {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Project-colour dot, 10px with a 2px white ring (§4.4). */
.quickBannerDot {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border-radius: 50%;
  background: var(--tile-color);
  box-shadow: 0 0 0 2px var(--white);
  vertical-align: -1px;
}

/* "连续" pill of the Locked state: 18px, white on the accent (6.29:1), with the lock icon. */
.quickBannerPill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 18px;
  margin: 0 8px;
  padding: 0 6px;
  border-radius: 9px;
  background: var(--accent);
  color: var(--white);
  font-size: 12px;
  font-weight: 600;
  vertical-align: -4px;
}

.quickBannerHint {
  margin-left: 12px;
  color: var(--text-muted);
}

/* Exit: accent text on the accent-soft bar (5.5:1); the Ghost variant's muted grey is only 4.8:1 there. */
.quickBanner .quickBannerExit {
  flex: none;
  height: var(--control-h-sm);
  padding: 0 10px;
  color: var(--accent);
  font-weight: 500;
}

/* Touch: 44px hit area for the exit button, extending into the 28px banner only (§4.4). */
@media (pointer: coarse) {
  .quickBanner .quickBannerExit {
    height: 44px;
    margin: -8px 0;
  }
}

.catchUp {
  width: 100%;
  min-height: 44px;
  border: 1px dashed var(--accent);
  border-radius: var(--radius-sm);
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.firstUse {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0 2px 2px;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.firstUseClose {
  flex: none;
  display: grid;
  place-items: center;
  width: var(--control-h-sm);
  height: var(--control-h-sm);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  font-size: 16px;
  line-height: 1;
}

.firstUseClose:hover {
  background: var(--surface-muted);
}

/* The completion time is a button that opens the completion-time editor (checkin-plan-change.md §3.2). */
.tickTimeButton {
  min-height: 24px;
  padding: 0 4px;
  border: none;
  border-radius: var(--radius-xs);
  background: none;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.tickTimeButton:hover,
.tickTimeButton:focus-visible {
  background: var(--surface-muted);
}

.tickTimeEmpty {
  color: var(--accent);
}

@media (pointer: coarse) {
  .tickTimeButton {
    min-height: 44px;
  }
}

.lateLine {
  margin: 2px 0 4px 21px;
  color: var(--text-muted);
  font-size: 12px;
}

.lateLink {
  padding: 0;
  border: none;
  background: none;
  color: var(--accent);
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
}

/* ---- check-in occurrences (checkin-occurrence-plan §3.5, §4) ---- */

/* The title row's "edit" and "+" buttons. */
.cardActions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.cardIconButton {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  touch-action: manipulation;
}

.cardIconButton:hover {
  background: var(--surface-muted);
  color: var(--text);
}

.cardIconButton:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -1px;
}

.cardIconButton svg {
  width: 16px;
  height: 16px;
}

@media (pointer: coarse) {
  .cardIconButton {
    width: 44px;
    height: 44px;
  }
}

/* Yesterday's overdue doses, above today's list. */
.overdue {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
}

/* The completion time is a button: a resting background and a dotted underline show it can be changed. */
.tickTimeButton {
  background: var(--surface-muted);
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}

.editMode {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.editRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 2px 4px;
}

.editRow input[type='checkbox'] {
  flex: none;
  width: 13px;
  height: 13px;
  margin: 0;
  accent-color: var(--accent);
}

.editRow.checkRowDone input[type='checkbox'] {
  accent-color: var(--success);
}

.editError {
  flex-basis: 100%;
  padding-left: 21px;
  color: var(--danger);
  font-size: 12px;
}

.editActions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (pointer: coarse) {
  .editRow {
    min-height: 44px;
  }

  .editRow input[type='checkbox'] {
    width: 16px;
    height: 16px;
  }
}
